// features/pharmacy/SubstitutionApprovalsDialog.tsx
// Where a doctor approves or rejects a pending drug substitution. Opened from
// the topbar bell (features/notifications/NotificationBell.tsx), which lists
// each pending request as a notification — it replaced the separate 🔀
// pending-approvals badge that used to sit beside it. Polling, not push: the
// bell's feed is what is polled; this dialog only loads its list while open.
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { EmptyState, LoadingState } from '@/components/ui/States';
import { usePendingSubstitutions, useApproveSubstitution, useRejectSubstitution } from './usePendingSubstitutions';
import type { SubstitutionReview } from './types';

function PendingSubstitutionRow({ row }: { row: SubstitutionReview }) {
  const approve = useApproveSubstitution();
  const reject = useRejectSubstitution();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <div className="rounded-[var(--radius)] border border-line p-2.5">
      <div className="text-[13px] font-semibold">{row.patient_name} <span className="font-normal text-ink-muted">({row.patient_uhid})</span></div>
      <div className="mt-1 text-[12.5px]">
        Dispense <strong>{row.dispensed_drug_name}{row.dispensed_strength ? ` ${row.dispensed_strength}` : ''}</strong> in place of{' '}
        <strong>{row.prescribed_drug_name}{row.prescribed_strength ? ` ${row.prescribed_strength}` : ''}</strong>
      </div>
      {row.reason && <div className="mt-1 text-[12px] text-ink-muted">Pharmacist's reason: {row.reason}</div>}
      {!rejecting ? (
        <div className="mt-2 flex gap-1.5">
          <Button size="sm" variant="success" disabled={approve.isPending} onClick={() => approve.mutate(row.id)}>
            {approve.isPending ? '…' : '✓ Approve'}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setRejecting(true)}>✕ Reject</Button>
        </div>
      ) : (
        <div className="mt-2 flex gap-1.5">
          <Input className="flex-1" placeholder="Reason for declining (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button
            size="sm" variant="danger" disabled={!reason.trim() || reject.isPending}
            onClick={() => reject.mutate({ id: row.id, reason: reason.trim() }, { onSuccess: () => { setRejecting(false); setReason(''); } })}
          >
            {reject.isPending ? '…' : 'Confirm'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

export function SubstitutionApprovalsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { user } = useAuth();
  // Doctor-only, like the backend (requireRole('Doctor')).
  const isDoctor = user?.role === 'Doctor';
  const listQuery = usePendingSubstitutions(isDoctor && open);
  if (!isDoctor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Pending drug-substitution approvals</DialogTitle>
        </DialogHeader>
        {listQuery.isLoading ? (
          <LoadingState />
        ) : !listQuery.data?.length ? (
          <EmptyState label="Nothing pending — every dispensed substitution has been reviewed." />
        ) : (
          <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
            {listQuery.data.map((r) => <PendingSubstitutionRow key={r.id} row={r} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
